// Auditoría de integridad del POS. POS → Control → Auditoría de ventas.
//
// Dos auditores de SÓLO LECTURA (scripts 891 y 893): no crean ni modifican
// nada, así que se pueden correr en producción sin miedo.
//
//   · Ventas  — que cabecera, detalles, impuestos, comprobante fiscal y pagos
//               cuadren entre sí al centavo.
//   · Cierres — que cada turno cerrado con facturas tenga su asiento contable
//               y que ese asiento cuadre. El POS contabiliza POR SESIÓN, no por
//               venta: buscar el asiento de una venta suelta no prueba nada.
//
// La tolerancia es de un centavo: por debajo es redondeo legítimo (el IVA se
// calcula por línea y se suma), por encima es un error.
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { AlertTriangle, CheckCircle2, ClipboardCheck, Landmark, Loader2, ShieldCheck } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { SortTh, useSort } from '@/features/control-bancario/shared';
import { Campo, fieldCls, money, posRpc } from './shared';
import { LOCALE } from '@/lib/utils/regional';

// ------------------------------------------------------------- Tipos RPC -----
interface AudVenta {
  venta: string; fecha: string; comprobante: string | null; estado_emision: string | null;
  con_recargo: boolean; total: number; subtotal: number; iva: number;
  suma_lineas: number; suma_iva_lineas: number; suma_pagos: number; vuelto: number;
  ticket_total: number | null; asiento: string | null;
  asiento_debe: number | null; asiento_haber: number | null; fallas: string[];
}
interface AudVentasResp {
  desde: string | null; hasta: string | null;
  ventas_revisadas: number; con_recargo_tarjeta: number;
  ventas_con_fallas: number; importe_con_fallas: number;
  resumen: Record<string, number>; detalle: AudVenta[];
}
interface AudCierre {
  sesion: string; caja: string | null; estado: string; abierta: string;
  n_ventas: number; ventas_total: number; facturado: number;
  asiento: string | null; asiento_debe: number | null; asiento_haber: number | null; fallas: string[];
}
interface AudCierresResp {
  sesiones: number; cerradas: number; con_fallas: number; ventas_sin_contabilizar: number;
  resumen: Record<string, number>; detalle: AudCierre[];
}

// Qué significa cada falla, en el idioma del negocio y no en el de la tabla.
const FALLAS: Record<string, { t: string; d: string; grave?: boolean }> = {
  linea_no_cuadra:     { t: 'La línea no cuadra', d: 'cantidad × precio − descuento no da el subtotal de esa línea.' },
  cabecera_vs_lineas:  { t: 'Cabecera ≠ líneas', d: 'El subtotal de la venta no es la suma de sus líneas.' },
  iva_vs_lineas:       { t: 'IVA ≠ líneas', d: 'El IVA de la venta no es la suma del IVA de sus líneas. Casi siempre es redondeo por línea frente a redondeo sobre el total.' },
  total_cabecera:      { t: 'Total incorrecto', d: 'El total no es subtotal + IVA.', grave: true },
  pagos_vs_total:      { t: 'Pagos ≠ total', d: 'Lo cobrado menos el vuelto no llega al total de la venta.', grave: true },
  ticket_vs_venta:     { t: 'Comprobante ≠ venta', d: 'El comprobante fiscal no coincide con la venta. Es lo más serio: al SRI se le declaró otra cifra.', grave: true },
  taxlines_vs_ticket:  { t: 'Impuestos ≠ comprobante', d: 'Lo que se declara en el F104 y el ATS no coincide con el comprobante.', grave: true },
  sin_asiento:         { t: 'Sin asiento contable', d: 'Turno cerrado con facturas que nunca llegó al mayor. Mira la bandeja de excepciones contables.', grave: true },
  asiento_descuadrado: { t: 'Asiento descuadrado', d: 'El asiento del cierre no cuadra debe = haber.', grave: true },
  asiento_vs_ventas:   { t: 'Asiento ≠ facturado', d: 'El asiento no mueve lo que facturó el turno (ya descontada la diferencia del arqueo).' },
  resumen_vs_ventas:   { t: 'Pagos ≠ vendido', d: 'El reparto por formas de pago no suma lo que vendió el turno.', grave: true },
};

function Chip({ f }: { f: string }) {
  const info = FALLAS[f];
  return (
    <span title={info?.d ?? f}
      className={`mr-1 inline-block rounded px-1.5 py-0.5 text-xs font-medium ${
        info?.grave ? 'bg-urgente-soft text-urgente' : 'bg-pendiente-soft text-pendiente'}`}>
      {info?.t ?? f}
    </span>
  );
}

function SinFallas({ que }: { que: string }) {
  return (
    <div className="flex flex-col items-center gap-2 py-12 text-center">
      <CheckCircle2 className="h-10 w-10 text-estable" />
      <p className="font-medium text-estable">Todo cuadra</p>
      <p className="text-sm text-muted-foreground">No hay {que} con diferencias en este rango.</p>
    </div>
  );
}

export function AuditoriaVentas() {
  const hoy = new Date().toISOString().slice(0, 10);
  const [tab, setTab] = useState<'ventas' | 'cierres'>('ventas');
  const [desde, setDesde] = useState(`${hoy.slice(0, 8)}01`);
  const [hasta, setHasta] = useState(hoy);
  const [soloFallas, setSoloFallas] = useState(true);

  const { data: aud, isFetching: fV } = useQuery<AudVentasResp>({
    queryKey: ['pos-auditar-ventas', desde, hasta, soloFallas],
    enabled: tab === 'ventas',
    staleTime: 0,
    queryFn: () => posRpc<AudVentasResp>('pos_auditar_ventas',
      { _desde: desde || null, _hasta: hasta || null, _solo_fallas: soloFallas }),
  });
  const { data: cie, isFetching: fC } = useQuery<AudCierresResp>({
    queryKey: ['pos-auditar-cierres', desde, hasta, soloFallas],
    enabled: tab === 'cierres',
    staleTime: 0,
    queryFn: () => posRpc<AudCierresResp>('pos_auditar_cierres',
      { _desde: desde || null, _hasta: hasta || null, _solo_fallas: soloFallas }),
  });

  const filasV = aud?.detalle ?? [];
  const filasC = cie?.detalle ?? [];
  const sortV = useSort(filasV, (r: AudVenta, k: string) =>
    k === 'total' ? Number(r.total) : k === 'fecha' ? r.fecha : (r as unknown as Record<string, string>)[k] ?? null);
  const sortC = useSort(filasC, (r: AudCierre, k: string) =>
    k === 'facturado' ? Number(r.facturado) : k === 'abierta' ? r.abierta : (r as unknown as Record<string, string>)[k] ?? null);

  const resumen = tab === 'ventas' ? aud?.resumen : cie?.resumen;
  const cargando = tab === 'ventas' ? fV : fC;

  return (
    <div className="space-y-4 p-6">
      <div className="flex items-center gap-3">
        <ShieldCheck className="h-7 w-7 text-primary" />
        <div>
          <h1 className="text-xl font-semibold">Auditoría de ventas y cierres</h1>
          <p className="text-sm text-muted-foreground">
            Comprueba que cabecera, detalles, impuestos, comprobante fiscal, pagos y contabilidad
            cuadren entre sí. Sólo lee: no cambia ni un dato.
          </p>
        </div>
      </div>

      <div className="flex flex-wrap items-end gap-2">
        <Button variant={tab === 'ventas' ? 'default' : 'outline'} size="sm" onClick={() => setTab('ventas')}>
          <ClipboardCheck className="mr-1 h-4 w-4" /> Integridad de ventas
        </Button>
        <Button variant={tab === 'cierres' ? 'default' : 'outline'} size="sm" onClick={() => setTab('cierres')}>
          <Landmark className="mr-1 h-4 w-4" /> Cierres y contabilización
        </Button>
        <Campo label="Desde" className="w-36">
          <input type="date" className={fieldCls} value={desde} onChange={(e) => setDesde(e.target.value)} />
        </Campo>
        <Campo label="Hasta" className="w-36">
          <input type="date" className={fieldCls} value={hasta} onChange={(e) => setHasta(e.target.value)} />
        </Campo>
        <Button size="sm" variant="outline" onClick={() => setSoloFallas((v) => !v)}>
          {soloFallas ? 'Ver también lo correcto' : 'Ver sólo las diferencias'}
        </Button>
        {cargando && <Loader2 className="mb-2 h-4 w-4 animate-spin text-primary" />}
      </div>

      {/* Tarjetas de cabecera */}
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        {tab === 'ventas' ? (
          <>
            <Tarjeta t="Ventas revisadas" v={aud?.ventas_revisadas ?? 0} />
            <Tarjeta t="Con recargo de tarjeta" v={aud?.con_recargo_tarjeta ?? 0} />
            <Tarjeta t="Con diferencias" v={aud?.ventas_con_fallas ?? 0} alerta={(aud?.ventas_con_fallas ?? 0) > 0} />
            <Tarjeta t="Importe implicado" v={money(aud?.importe_con_fallas ?? 0)} />
          </>
        ) : (
          <>
            <Tarjeta t="Sesiones" v={cie?.sesiones ?? 0} />
            <Tarjeta t="Cerradas" v={cie?.cerradas ?? 0} />
            <Tarjeta t="Con diferencias" v={cie?.con_fallas ?? 0} alerta={(cie?.con_fallas ?? 0) > 0} />
            <Tarjeta t="Sin contabilizar" v={money(cie?.ventas_sin_contabilizar ?? 0)}
                     alerta={(cie?.ventas_sin_contabilizar ?? 0) > 0} />
          </>
        )}
      </div>

      {/* Qué tipos de diferencia hay, para saber por dónde empezar */}
      {resumen && Object.keys(resumen).length > 0 && (
        <Card>
          <CardHeader className="pb-2"><CardTitle className="flex items-center gap-2 text-base">
            <AlertTriangle className="h-4 w-4 text-urgente" /> Qué está fallando
          </CardTitle></CardHeader>
          <CardContent className="space-y-1.5">
            {Object.entries(resumen).sort((a, b) => b[1] - a[1]).map(([k, n]) => (
              <div key={k} className="flex items-start gap-2 text-sm">
                <span className="w-10 text-right font-mono tabular-nums font-semibold">{n}</span>
                <span><Chip f={k} /><span className="text-muted-foreground">{FALLAS[k]?.d ?? ''}</span></span>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      <Card>
        <CardContent className="overflow-x-auto pt-4">
          {tab === 'ventas' ? (
            filasV.length === 0 && !fV ? <SinFallas que="ventas" /> : (
            <table className="w-full text-sm">
              <thead><tr className="border-b text-left text-xs text-muted-foreground">
                <th className="py-2 pr-3 text-right">N°</th>
                <SortTh col="venta" sort={sortV}>Venta</SortTh>
                <SortTh col="fecha" sort={sortV}>Fecha</SortTh>
                <SortTh col="comprobante" sort={sortV}>Comprobante</SortTh>
                <SortTh col="total" sort={sortV} align="right">Total</SortTh>
                <th className="py-2 pr-3 text-right">Σ líneas</th>
                <th className="py-2 pr-3 text-right">IVA vs Σ IVA</th>
                <th className="py-2 pr-3 text-right">Comprobante</th>
                <th className="py-2 pr-3">Diferencias</th>
              </tr></thead>
              <tbody>
                {sortV.sorted.map((r, i) => (
                  <tr key={r.venta} className={`border-b last:border-0 hover:bg-muted/40 ${r.fallas.some((f) => FALLAS[f]?.grave) ? 'bg-urgente-soft/40' : ''}`}>
                    <td className="py-1.5 pr-3 text-right text-muted-foreground">{i + 1}</td>
                    <td className="py-1.5 pr-3 font-mono text-xs">{r.venta}</td>
                    <td className="py-1.5 pr-3 whitespace-nowrap">{new Date(r.fecha).toLocaleDateString(LOCALE)}</td>
                    <td className="py-1.5 pr-3 font-mono text-xs">{r.comprobante ?? '—'}</td>
                    <td className="py-1.5 pr-3 text-right font-mono tabular-nums font-medium">{money(r.total)}</td>
                    <td className="py-1.5 pr-3 text-right font-mono tabular-nums">
                      {money(r.subtotal)} <span className="text-muted-foreground">/ {money(r.suma_lineas)}</span>
                    </td>
                    <td className="py-1.5 pr-3 text-right font-mono tabular-nums">
                      {money(r.iva)} <span className="text-muted-foreground">/ {money(r.suma_iva_lineas)}</span>
                    </td>
                    <td className="py-1.5 pr-3 text-right font-mono tabular-nums">
                      {r.ticket_total == null ? '—' : money(r.ticket_total)}
                    </td>
                    <td className="py-1.5 pr-3">{r.fallas.length ? r.fallas.map((f) => <Chip key={f} f={f} />)
                      : <span className="text-xs text-estable">cuadra</span>}</td>
                  </tr>
                ))}
              </tbody>
            </table>)
          ) : (
            filasC.length === 0 && !fC ? <SinFallas que="cierres" /> : (
            <table className="w-full text-sm">
              <thead><tr className="border-b text-left text-xs text-muted-foreground">
                <th className="py-2 pr-3 text-right">N°</th>
                <SortTh col="sesion" sort={sortC}>Sesión</SortTh>
                <SortTh col="caja" sort={sortC}>Caja</SortTh>
                <SortTh col="abierta" sort={sortC}>Abierta</SortTh>
                <SortTh col="estado" sort={sortC}>Estado</SortTh>
                <th className="py-2 pr-3 text-right">Ventas</th>
                <SortTh col="facturado" sort={sortC} align="right">Facturado</SortTh>
                <th className="py-2 pr-3">Asiento</th>
                <th className="py-2 pr-3 text-right">Debe / Haber</th>
                <th className="py-2 pr-3">Diferencias</th>
              </tr></thead>
              <tbody>
                {sortC.sorted.map((r, i) => (
                  <tr key={r.sesion} className={`border-b last:border-0 hover:bg-muted/40 ${r.fallas.some((f) => FALLAS[f]?.grave) ? 'bg-urgente-soft/40' : ''}`}>
                    <td className="py-1.5 pr-3 text-right text-muted-foreground">{i + 1}</td>
                    <td className="py-1.5 pr-3 font-mono text-xs">{r.sesion}</td>
                    <td className="py-1.5 pr-3">{r.caja ?? '—'}</td>
                    <td className="py-1.5 pr-3 whitespace-nowrap">{new Date(r.abierta).toLocaleDateString(LOCALE)}</td>
                    <td className="py-1.5 pr-3 capitalize">{r.estado}</td>
                    <td className="py-1.5 pr-3 text-right font-mono tabular-nums">{r.n_ventas}</td>
                    <td className="py-1.5 pr-3 text-right font-mono tabular-nums font-medium">{money(r.facturado)}</td>
                    <td className="py-1.5 pr-3 font-mono text-xs">{r.asiento ?? <span className="text-urgente">— ninguno —</span>}</td>
                    <td className="py-1.5 pr-3 text-right font-mono tabular-nums">
                      {r.asiento_debe == null ? '—' : `${money(r.asiento_debe)} / ${money(r.asiento_haber ?? 0)}`}
                    </td>
                    <td className="py-1.5 pr-3">{r.fallas.length ? r.fallas.map((f) => <Chip key={f} f={f} />)
                      : <span className="text-xs text-estable">cuadra</span>}</td>
                  </tr>
                ))}
              </tbody>
            </table>)
          )}
        </CardContent>
      </Card>

      <p className="text-xs text-muted-foreground">
        Tolerancia de un centavo: por debajo es redondeo legítimo (el IVA se calcula por línea y se suma),
        por encima es un error. El POS contabiliza <strong>por sesión de caja</strong>, no por venta, y las
        sesiones que sólo vendieron notas de venta no llevan asiento: eso es correcto y no se marca.
      </p>
    </div>
  );
}

function Tarjeta({ t, v, alerta }: { t: string; v: number | string; alerta?: boolean }) {
  return (
    <div className={`rounded-lg border p-3 ${alerta ? 'border-urgente/40 bg-urgente-soft' : 'bg-card'}`}>
      <p className="text-xs text-muted-foreground">{t}</p>
      <p className={`text-xl font-bold font-mono tabular-nums ${alerta ? 'text-urgente' : ''}`}>{v}</p>
    </div>
  );
}
